import { useState, useEffect } from "react";
import { mormSigner, AgentDelegationResult, KeypairResult, ChainType } from "../sdk";
import { generateAddressFromMnemonic } from "../utils/addressGeneration";
import type { UseOnboardResult } from "./useOnboard";
import {
  // Type exports
  type SupportedWallet,
  type EthereumProvider,
  // Detection functions (only import those used in this file)
  detectSupportedWallet,
  isSupportedWallet,
} from "../sdk/utils/extdetection";

// localStorage keys (shared with useOnboard for integration)
const STORAGE_KEY_MNEMONIC = "onboard_mnemonic";
const STORAGE_KEY_WALLET_ADDRESS = "onboard_wallet_address";
const STORAGE_KEY_CHAIN_TYPE = "onboard_chain_type";
import { useAccount, useSignTypedData } from "wagmi";
import {
  isPhantomSolanaAvailable,
  getPhantomSolanaProvider,
  connectPhantomSolana,
  signSolanaMessage,
  type SolanaMessagePayload,
} from "../sdk/signing/solana";
import {
  isBitcoinWalletAvailable,
  getPreferredBitcoinWallet,
  signBitcoinMessage,
  getBitcoinAddress,
  type BitcoinMessagePayload,
  type OKXBitcoinProvider,
} from "../sdk/signing/bitcoin";

// Re-export types for backward compatibility
export type { SupportedWallet, EthereumProvider } from "../sdk/utils/extdetection";

// sessionStorage keys for agent credentials (temporary browser storage)
const STORAGE_KEY_AGENT_ADDRESS = "agent_delegation_address";
const STORAGE_KEY_AGENT_PRIVATE_KEY = "agent_delegation_private_key";
const STORAGE_KEY_AGENT_EXPIRES_AT = "agent_delegation_expires_at";
const STORAGE_KEY_AGENT_OWNER_ADDRESS = "agent_delegation_owner_address"; // Track which owner this agent belongs to

// All wallet detection functions are now imported from extdetection.ts
// Re-exported for backward compatibility
export {
  isMetaMaskProviderAvailable,
  verifyMetaMaskProvider,
  isBinanceProviderAvailable,
  waitForBinanceWallet,
  getBinanceProvider,
  isBitgetProviderAvailable,
  getBitgetProvider,
  waitForBitgetWallet,
  isTokenPocketProviderAvailable,
  isTokenPocketEthereumAvailable,
  isTokenPocketSolanaAvailable,
  getTokenPocketProvider,
  waitForTokenPocketWallet,
  isCoinbaseWalletProviderAvailable,
  isCoinbaseWalletProviderAvailableSync,
  getCoinbaseWalletProvider,
  waitForCoinbaseWallet,
  isCoinbaseWalletSolanaAvailable,
  detectSupportedWallet,
  isSupportedWallet,
} from "../sdk/utils/extdetection";

/**
 * Custom hook for managing agent delegation operations
 *
 * Handles the complete agent delegation workflow:
 * - Generating owner address from mnemonic (supports multichain)
 * - Generating agent keypair
 * - Preparing agent sign (EIP-712 typed data)
 * - Signing agent approval
 *
 * @param onboardHook - Optional useOnboard hook result to integrate with persisted account data
 * @param initialOwnerMnemonic - Initial owner mnemonic phrase (default: from useOnboard or localStorage, fallback: standard test mnemonic)
 * @param initialExpiresInDays - Initial expiration days (default: 180)
 * @param initialPermissions - Initial permissions string (default: 'clob::submit_order,clob::cancel_order')
 * @param initialAgentName - Initial agent name (default: 'TradingBot')
 * @param initialChainType - Initial chain type for owner address (default: from useOnboard or 'ethereum')
 * @param initialAddressIndex - Initial address index for derivation (default: 0)
 * @param initialIsMainnet - Initial network type (default: true)
 * @returns Object containing:
 *   - ownerMnemonic: Current owner mnemonic phrase
 *   - setOwnerMnemonic: Function to update owner mnemonic
 *   - ownerAddress: Generated owner address
 *   - setOwnerAddress: Function to update owner address
 *   - ownerChainType: Current chain type for owner address
 *   - setOwnerChainType: Function to update chain type
 *   - ownerAddressIndex: Current address index for derivation
 *   - setOwnerAddressIndex: Function to update address index
 *   - ownerIsMainnet: Current network type (mainnet/testnet)
 *   - setOwnerIsMainnet: Function to update network type
 *   - agentAddress: Generated agent address
 *   - setAgentAddress: Function to update agent address
 *   - agentPrivateKey: Generated agent private key
 *   - setAgentPrivateKey: Function to update agent private key
 *   - expiresInDays: Expiration days for delegation
 *   - setExpiresInDays: Function to update expiration days
 *   - permissions: Permissions string (comma-separated)
 *   - setPermissions: Function to update permissions
 *   - agentName: Agent name
 *   - setAgentName: Function to update agent name
 *   - nonce: Nonce for delegation
 *   - setNonce: Function to update nonce
 *   - result: Delegation result or error
 *   - loading: Boolean indicating if operation is in progress
 *   - generateOwnerAddress: Function to generate owner address from mnemonic (uses current chainType)
 *   - generateOwnerAddressForChain: Function to generate owner address for specific chain type
 *   - generateAgentKeypair: Function to generate agent keypair
 *   - prepareAgentSign: Function to prepare agent sign (EIP-712 typed data)
 *   - signAgentApproval: Function to sign agent approval
 *   - isReady: Boolean indicating if mormSigner is ready
 */
export function useAgentDelegation(
  onboardHook?: UseOnboardResult,
  initialOwnerMnemonic?: string,
  initialExpiresInDays: number = 180,
  initialPermissions: string = "clob::submit_order,clob::cancel_order",
  initialAgentName: string = "BrowserUserAgent",
  initialChainType?: ChainType,
  initialAddressIndex: number = 0,
  initialIsMainnet: boolean = true,
) {
  // Get mnemonic from useOnboard hook, localStorage, or fallback to initial value
  const getInitialMnemonic = (): string => {
    if (onboardHook?.mnemonic) return onboardHook.mnemonic;
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(STORAGE_KEY_MNEMONIC);
      if (stored) return stored;
    }
    return initialOwnerMnemonic || "abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon about";
  };

  // Get chain type from useOnboard hook, localStorage, or fallback to initial value
  const getInitialChainType = (): ChainType => {
    if (onboardHook?.chainType) return onboardHook.chainType;
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(STORAGE_KEY_CHAIN_TYPE);
      if (stored && ["ethereum", "bitcoin_taproot", "bitcoin_segwit", "bitcoin_legacy", "bitcoin_nested_segwit", "solana"].includes(stored)) {
        return stored as ChainType;
      }
    }
    return initialChainType || "ethereum";
  };

  // Get wallet address from useOnboard hook or localStorage
  const getInitialOwnerAddress = (): string => {
    if (onboardHook?.walletAddress) return onboardHook.walletAddress;
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(STORAGE_KEY_WALLET_ADDRESS);
      if (stored) return stored;
    }
    return "";
  };

  const [ownerMnemonic, setOwnerMnemonic] =
    useState<string>(getInitialMnemonic());
  const [ownerAddress, setOwnerAddress] = useState<string>(getInitialOwnerAddress());
  const [ownerChainType, setOwnerChainType] = useState<ChainType>(getInitialChainType());
  const [ownerAddressIndex, setOwnerAddressIndex] = useState<number>(initialAddressIndex);
  const [ownerIsMainnet, setOwnerIsMainnet] = useState<boolean>(initialIsMainnet);
  const [agentAddress, setAgentAddress] = useState<string>("");
  const [agentPrivateKey, setAgentPrivateKey] = useState<string>("");
  const [expiresInDays, setExpiresInDays] =
    useState<number>(initialExpiresInDays);
  const [permissions, setPermissions] = useState<string>(initialPermissions);
  const [agentName, setAgentName] = useState<string>(initialAgentName);
  const [nonce, setNonce] = useState<string>(Date.now().toString());
  const [result, setResult] = useState<AgentDelegationResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Sync with useOnboard hook if provided (single source of truth integration)
  useEffect(() => {
    if (onboardHook) {
      // Update mnemonic from useOnboard if it changes
      if (onboardHook.mnemonic && onboardHook.mnemonic !== ownerMnemonic) {
        setOwnerMnemonic(onboardHook.mnemonic);
      }
      // Update address from useOnboard if it changes and chain type matches
      if (onboardHook.walletAddress && 
          onboardHook.chainType === ownerChainType &&
          onboardHook.walletAddress !== ownerAddress) {
        setOwnerAddress(onboardHook.walletAddress);
      }
      // Update chain type from useOnboard if it changes
      if (onboardHook.chainType && onboardHook.chainType !== ownerChainType) {
        setOwnerChainType(onboardHook.chainType);
      }
    }
  }, [onboardHook?.mnemonic, onboardHook?.walletAddress, onboardHook?.chainType, ownerMnemonic, ownerAddress, ownerChainType]);

  // Restore agent credentials from sessionStorage on mount
  useEffect(() => {
    try {
      const storedAddress = sessionStorage.getItem(STORAGE_KEY_AGENT_ADDRESS);
      const storedPrivateKey = sessionStorage.getItem(STORAGE_KEY_AGENT_PRIVATE_KEY);
      const storedExpiresAt = sessionStorage.getItem(STORAGE_KEY_AGENT_EXPIRES_AT);
      const storedOwnerAddress = sessionStorage.getItem(STORAGE_KEY_AGENT_OWNER_ADDRESS);

      // Only restore if credentials exist and haven't expired
      if (storedAddress && storedPrivateKey && storedExpiresAt) {
        const expiresAt = parseInt(storedExpiresAt, 10);
        const now = Date.now();

        // Check if credentials are still valid (not expired)
        if (!isNaN(expiresAt) && now < expiresAt) {
          // Only restore if owner address matches (credentials are tied to specific owner)
          if (!storedOwnerAddress || !ownerAddress || storedOwnerAddress.toLowerCase() === ownerAddress.toLowerCase()) {
            setAgentAddress(storedAddress);
            setAgentPrivateKey(storedPrivateKey);
          } else {
            // Owner address changed, clear old credentials
            sessionStorage.removeItem(STORAGE_KEY_AGENT_ADDRESS);
            sessionStorage.removeItem(STORAGE_KEY_AGENT_PRIVATE_KEY);
            sessionStorage.removeItem(STORAGE_KEY_AGENT_EXPIRES_AT);
            sessionStorage.removeItem(STORAGE_KEY_AGENT_OWNER_ADDRESS);
          }
        } else {
          // Expired, clear storage
          sessionStorage.removeItem(STORAGE_KEY_AGENT_ADDRESS);
          sessionStorage.removeItem(STORAGE_KEY_AGENT_PRIVATE_KEY);
          sessionStorage.removeItem(STORAGE_KEY_AGENT_EXPIRES_AT);
          sessionStorage.removeItem(STORAGE_KEY_AGENT_OWNER_ADDRESS);
        }
      }
    } catch (error) {
      // sessionStorage might not be available (e.g., in private browsing)
      console.warn("Failed to restore agent credentials from sessionStorage:", error);
    }
  }, []); // Only run on mount

  // Clear agent credentials when owner address changes (credentials are tied to owner)
  useEffect(() => {
    if (ownerAddress) {
      const storedOwnerAddress = sessionStorage.getItem(STORAGE_KEY_AGENT_OWNER_ADDRESS);
      // If owner address changed, clear old agent credentials
      if (storedOwnerAddress && storedOwnerAddress.toLowerCase() !== ownerAddress.toLowerCase()) {
        sessionStorage.removeItem(STORAGE_KEY_AGENT_ADDRESS);
        sessionStorage.removeItem(STORAGE_KEY_AGENT_PRIVATE_KEY);
        sessionStorage.removeItem(STORAGE_KEY_AGENT_EXPIRES_AT);
        sessionStorage.removeItem(STORAGE_KEY_AGENT_OWNER_ADDRESS);
        setAgentAddress("");
        setAgentPrivateKey("");
      }
    }
  }, [ownerAddress]);

  // Generate owner address using current chain type (backward compatible)
  const generateOwnerAddress = async (): Promise<void> => {
    await generateOwnerAddressForChain(ownerChainType, ownerAddressIndex, ownerIsMainnet);
  };

  // Generate owner address for specific chain type (multichain support)
  // Uses shared address generation utility to eliminate duplication
  const generateOwnerAddressForChain = async (
    chainType: ChainType = ownerChainType,
    addressIndex: number = ownerAddressIndex,
    isMainnet: boolean = ownerIsMainnet,
  ): Promise<void> => {
    setLoading(true);
    try {
      const addressResult = await generateAddressFromMnemonic(
        ownerMnemonic,
        chainType,
        addressIndex,
        isMainnet,
      );

      if (addressResult.success && addressResult.address) {
        setOwnerAddress(addressResult.address);
        // Update chain type state if different
        if (chainType !== ownerChainType) {
          setOwnerChainType(chainType);
        }
        if (addressIndex !== ownerAddressIndex) {
          setOwnerAddressIndex(addressIndex);
        }
        if (isMainnet !== ownerIsMainnet) {
          setOwnerIsMainnet(isMainnet);
        }
      } else {
        setResult({
          success: false,
          error: addressResult.error || "Failed to generate owner address",
        });
      }
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  };

  const generateAgentKeypair = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    setLoading(true);
    try {
      const keypairResult: KeypairResult = await mormSigner.generateKeypair();
      if (
        keypairResult.success &&
        keypairResult.address &&
        keypairResult.privateKey
      ) {
        setAgentAddress(keypairResult.address);
        setAgentPrivateKey(keypairResult.privateKey);

        // Store agent credentials in sessionStorage (temporary browser storage)
        // These will be automatically cleared when browser closes
        try {
          const expirationTime = Date.now() + (expiresInDays * 24 * 60 * 60 * 1000); // Expires in N days
          sessionStorage.setItem(STORAGE_KEY_AGENT_ADDRESS, keypairResult.address);
          sessionStorage.setItem(STORAGE_KEY_AGENT_PRIVATE_KEY, keypairResult.privateKey);
          sessionStorage.setItem(STORAGE_KEY_AGENT_EXPIRES_AT, expirationTime.toString());
          if (ownerAddress) {
            sessionStorage.setItem(STORAGE_KEY_AGENT_OWNER_ADDRESS, ownerAddress);
          }
        } catch (storageError) {
          // sessionStorage might not be available (e.g., in private browsing)
          console.warn("Failed to store agent credentials in sessionStorage:", storageError);
          // Continue anyway - credentials are still in state
        }
      } else {
        setResult({
          success: false,
          error: keypairResult.error || "Failed to generate agent keypair",
        });
      }
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  };

  const prepareAgentSign = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner and agent addresses are required",
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      const result = await mormSigner.prepareAgentSign({
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
      });

      setResult(result);
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Wagmi hooks for all wallet support (MetaMask, Phantom, OneKey)
  const { address: connectedAddress, isConnected, connector } = useAccount();
  const { signTypedDataAsync, isPending: isSigning } = useSignTypedData();

  // Helper to get the connected wallet type
  const getConnectedWalletType = (): SupportedWallet | null => {
    if (!isConnected || !connector) return null;

    const detected = detectSupportedWallet();
    if (isSupportedWallet(detected)) {
      return detected;
    }

    // Fallback: check connector name
    const connectorName = connector.name?.toLowerCase() || "";
    if (connectorName.includes("phantom")) return "phantom";
    if (connectorName.includes("onekey")) return "onekey";
    if (connectorName.includes("okx")) return "okx";
    if (connectorName.includes("binance")) return "binance";
    if (connectorName.includes("bitget") || connectorName.includes("bitkeep")) return "bitget";
    if (connectorName.includes("tokenpocket") || connectorName.includes("tp")) return "tokenpocket";
    if (connectorName.includes("coinbase")) return "coinbase";
    if (connectorName.includes("rainbow")) return "rainbow";
    if (connectorName.includes("metamask")) return "metamask";

    return null;
  };

  const signAgentApproval = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner address and agent address are required",
      });
      return;
    }

    // Check if wallet is connected
    if (!isConnected || !connectedAddress) {
      setResult({
        success: false,
        error: "Please connect your MetaMask wallet first",
      });
      return;
    }

    // Verify it's actually MetaMask
    const walletType = getConnectedWalletType();
    if (walletType !== "metamask") {
      const detected = detectSupportedWallet();
      if (detected === "metamask") {
        setResult({
          success: false,
          error:
            'MetaMask wallet detected but not connected via wagmi. Please use the "Connect MetaMask" button first.',
        });
      } else {
        setResult({
          success: false,
          error:
            "This function is for MetaMask wallet only. Please use the appropriate button for your wallet.",
        });
      }
      return;
    }

    // Verify the connected address matches the owner address
    if (connectedAddress.toLowerCase() !== ownerAddress.toLowerCase()) {
      setResult({
        success: false,
        error: `Connected address (${connectedAddress}) does not match owner address (${ownerAddress}). Please switch to the correct account in MetaMask.`,
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      // First, prepare the typed data
      const prepareResult = await mormSigner.prepareAgentSign({
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
      });

      if (!prepareResult.success || !prepareResult.typedData) {
        setResult(prepareResult);
        return;
      }

      // Convert typed data to wagmi format
      const typedData = prepareResult.typedData as any;

      let domain, types, primaryType, message;

      if (typedData.domain && typedData.types && typedData.message) {
        domain = typedData.domain;
        types = typedData.types;
        primaryType =
          typedData.primaryType || typedData.primaryType || "AgentApproval";
        message = typedData.message;
      } else {
        domain = typedData.domain || {};
        types = typedData.types || typedData.Types || {};
        primaryType =
          typedData.primaryType || typedData.PrimaryType || "AgentApproval";
        message = typedData.message || typedData.Message || typedData;
      }

      // Sign with wagmi (supports MetaMask via injected connector)
      const signature = await signTypedDataAsync({
        domain,
        types,
        primaryType,
        message,
      });

      setResult({
        success: true,
        typedData: prepareResult.typedData,
        signature: signature,
      });
    } catch (error: any) {
      // Handle user rejection
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected")
      ) {
        setResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        setResult({
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Unknown error during MetaMask signing",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const signAgentApprovalWithPhantom = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner address and agent address are required",
      });
      return;
    }

    // Check if wallet is connected
    if (!isConnected || !connectedAddress) {
      setResult({
        success: false,
        error: "Please connect your Phantom wallet first",
      });
      return;
    }

    // Verify it's actually Phantom
    const walletType = getConnectedWalletType();
    if (walletType !== "phantom") {
      const detected = detectSupportedWallet();
      if (detected === "phantom") {
        setResult({
          success: false,
          error:
            'Phantom wallet detected but not connected via wagmi. Please use the "Connect Phantom" button first.',
        });
      } else {
        setResult({
          success: false,
          error:
            "This function is for Phantom wallet only. Please use the appropriate button for your wallet.",
        });
      }
      return;
    }

    // Verify the connected address matches the owner address
    if (connectedAddress.toLowerCase() !== ownerAddress.toLowerCase()) {
      setResult({
        success: false,
        error: `Connected address (${connectedAddress}) does not match owner address (${ownerAddress}). Please switch to the correct account in Phantom.`,
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      // First, prepare the typed data
      const prepareResult = await mormSigner.prepareAgentSign({
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
      });

      if (!prepareResult.success || !prepareResult.typedData) {
        setResult(prepareResult);
        return;
      }

      // Convert typed data to wagmi format
      // wagmi expects { domain, types, primaryType, message }
      const typedData = prepareResult.typedData as any;

      // Extract the EIP-712 structure
      // The typedData might be in different formats, so we handle both
      let domain, types, primaryType, message;

      if (typedData.domain && typedData.types && typedData.message) {
        // Already in the correct format
        domain = typedData.domain;
        types = typedData.types;
        primaryType =
          typedData.primaryType || typedData.primaryType || "AgentApproval";
        message = typedData.message;
      } else {
        // Try to extract from the structure
        // This handles cases where the WASM returns a different format
        domain = typedData.domain || {};
        types = typedData.types || typedData.Types || {};
        primaryType =
          typedData.primaryType || typedData.PrimaryType || "AgentApproval";
        message = typedData.message || typedData.Message || typedData;
      }

      // Sign with wagmi (supports Phantom via injected connector)
      const signature = await signTypedDataAsync({
        domain,
        types,
        primaryType,
        message,
      });

      setResult({
        success: true,
        typedData: prepareResult.typedData,
        signature: signature,
      });
    } catch (error: any) {
      // Handle user rejection
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected")
      ) {
        setResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        setResult({
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Unknown error during Phantom signing",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const signAgentApprovalWithOneKey = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner address and agent address are required",
      });
      return;
    }

    // Check if wallet is connected
    if (!isConnected || !connectedAddress) {
      setResult({
        success: false,
        error: "Please connect your OneKey wallet first",
      });
      return;
    }

    // Verify it's actually OneKey
    const walletType = getConnectedWalletType();
    if (walletType !== "onekey") {
      const detected = detectSupportedWallet();
      if (detected === "onekey") {
        setResult({
          success: false,
          error:
            'OneKey wallet detected but not connected via wagmi. Please use the "Connect OneKey" button first.',
        });
      } else {
        setResult({
          success: false,
          error:
            "This function is for OneKey wallet only. Please use the appropriate button for your wallet.",
        });
      }
      return;
    }

    // Verify the connected address matches the owner address
    if (connectedAddress.toLowerCase() !== ownerAddress.toLowerCase()) {
      setResult({
        success: false,
        error: `Connected address (${connectedAddress}) does not match owner address (${ownerAddress}). Please switch to the correct account in OneKey.`,
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      // First, prepare the typed data
      const prepareResult = await mormSigner.prepareAgentSign({
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
      });

      if (!prepareResult.success || !prepareResult.typedData) {
        setResult(prepareResult);
        return;
      }

      // Convert typed data to wagmi format
      const typedData = prepareResult.typedData as any;

      let domain, types, primaryType, message;

      if (typedData.domain && typedData.types && typedData.message) {
        domain = typedData.domain;
        types = typedData.types;
        primaryType =
          typedData.primaryType || typedData.primaryType || "AgentApproval";
        message = typedData.message;
      } else {
        domain = typedData.domain || {};
        types = typedData.types || typedData.Types || {};
        primaryType =
          typedData.primaryType || typedData.PrimaryType || "AgentApproval";
        message = typedData.message || typedData.Message || typedData;
      }

      // Sign with wagmi (supports OneKey via injected connector)
      const signature = await signTypedDataAsync({
        domain,
        types,
        primaryType,
        message,
      });

      setResult({
        success: true,
        typedData: prepareResult.typedData,
        signature: signature,
      });
    } catch (error: any) {
      // Handle user rejection
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected")
      ) {
        setResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        setResult({
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Unknown error during OneKey signing",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Sign agent approval with Bitget wallet
  const signAgentApprovalWithBitget = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner address and agent address are required",
      });
      return;
    }

    // Check if wallet is connected
    if (!isConnected || !connectedAddress) {
      setResult({
        success: false,
        error: "Please connect your Bitget wallet first",
      });
      return;
    }

    // Verify it's actually Bitget
    const walletType = getConnectedWalletType();
    if (walletType !== "bitget") {
      const detected = detectSupportedWallet();
      if (detected === "bitget") {
        setResult({
          success: false,
          error:
            'Bitget wallet detected but not connected via wagmi. Please use the "Connect Bitget" button first.',
        });
      } else {
        setResult({
          success: false,
          error:
            "This function is for Bitget wallet only. Please use the appropriate button for your wallet.",
        });
      }
      return;
    }

    // Verify the connected address matches the owner address
    if (connectedAddress.toLowerCase() !== ownerAddress.toLowerCase()) {
      setResult({
        success: false,
        error: `Connected address (${connectedAddress}) does not match owner address (${ownerAddress}). Please switch to the correct account in Bitget.`,
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      // First, prepare the typed data
      const prepareResult = await mormSigner.prepareAgentSign({
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
      });

      if (!prepareResult.success || !prepareResult.typedData) {
        setResult(prepareResult);
        return;
      }

      // Convert typed data to wagmi format
      const typedData = prepareResult.typedData as any;

      let domain, types, primaryType, message;

      if (typedData.domain && typedData.types && typedData.message) {
        domain = typedData.domain;
        types = typedData.types;
        primaryType =
          typedData.primaryType || typedData.primaryType || "AgentApproval";
        message = typedData.message;
      } else {
        domain = typedData.domain || {};
        types = typedData.types || typedData.Types || {};
        primaryType =
          typedData.primaryType || typedData.PrimaryType || "AgentApproval";
        message = typedData.message || typedData.Message || typedData;
      }

      // Sign with wagmi (supports Bitget via injected connector)
      const signature = await signTypedDataAsync({
        domain,
        types,
        primaryType,
        message,
      });

      setResult({
        success: true,
        typedData: prepareResult.typedData,
        signature: signature,
      });
    } catch (error: any) {
      // Handle user rejection
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected")
      ) {
        setResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        setResult({
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Unknown error during Bitget signing",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Sign agent approval with TokenPocket wallet
  const signAgentApprovalWithTokenPocket = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner address and agent address are required",
      });
      return;
    }

    // Check if wallet is connected
    if (!isConnected || !connectedAddress) {
      setResult({
        success: false,
        error: "Please connect your TokenPocket wallet first",
      });
      return;
    }

    // Verify it's actually TokenPocket
    const walletType = getConnectedWalletType();
    if (walletType !== "tokenpocket") {
      const detected = detectSupportedWallet();
      if (detected === "tokenpocket") {
        setResult({
          success: false,
          error:
            'TokenPocket wallet detected but not connected via wagmi. Please use the "Connect TokenPocket" button first.',
        });
      } else {
        setResult({
          success: false,
          error:
            "This function is for TokenPocket wallet only. Please use the appropriate button for your wallet.",
        });
      }
      return;
    }

    // Verify the connected address matches the owner address
    if (connectedAddress.toLowerCase() !== ownerAddress.toLowerCase()) {
      setResult({
        success: false,
        error: `Connected address (${connectedAddress}) does not match owner address (${ownerAddress}). Please switch to the correct account in TokenPocket.`,
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      // First, prepare the typed data
      const prepareResult = await mormSigner.prepareAgentSign({
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
      });

      if (!prepareResult.success || !prepareResult.typedData) {
        setResult(prepareResult);
        return;
      }

      // Convert typed data to wagmi format
      const typedData = prepareResult.typedData as any;

      let domain, types, primaryType, message;

      if (typedData.domain && typedData.types && typedData.message) {
        domain = typedData.domain;
        types = typedData.types;
        primaryType =
          typedData.primaryType || typedData.primaryType || "AgentApproval";
        message = typedData.message;
      } else {
        domain = typedData.domain || {};
        types = typedData.types || typedData.Types || {};
        primaryType =
          typedData.primaryType || typedData.PrimaryType || "AgentApproval";
        message = typedData.message || typedData.Message || typedData;
      }

      // Sign with wagmi (supports TokenPocket via injected connector)
      const signature = await signTypedDataAsync({
        domain,
        types,
        primaryType,
        message,
      });

      setResult({
        success: true,
        typedData: prepareResult.typedData,
        signature: signature,
      });
    } catch (error: any) {
      // Handle user rejection
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected")
      ) {
        setResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        setResult({
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Unknown error during TokenPocket signing",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Sign agent approval with Coinbase Wallet
  const signAgentApprovalWithCoinbaseWallet = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner address and agent address are required",
      });
      return;
    }

    // Check if wallet is connected
    if (!isConnected || !connectedAddress) {
      setResult({
        success: false,
        error: "Please connect your Coinbase Wallet first",
      });
      return;
    }

    // Verify it's actually Coinbase Wallet
    const walletType = getConnectedWalletType();
    if (walletType !== "coinbase") {
      const detected = detectSupportedWallet();
      if (detected === "coinbase") {
        setResult({
          success: false,
          error:
            'Coinbase Wallet detected but not connected via wagmi. Please use the "Connect Coinbase Wallet" button first.',
        });
      } else {
        setResult({
          success: false,
          error:
            "This function is for Coinbase Wallet only. Please use the appropriate button for your wallet.",
        });
      }
      return;
    }

    // Verify the connected address matches the owner address
    if (connectedAddress.toLowerCase() !== ownerAddress.toLowerCase()) {
      setResult({
        success: false,
        error: `Connected address (${connectedAddress}) does not match owner address (${ownerAddress}). Please switch to the correct account in Coinbase Wallet.`,
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      // First, prepare the typed data
      const prepareResult = await mormSigner.prepareAgentSign({
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
      });

      if (!prepareResult.success || !prepareResult.typedData) {
        setResult(prepareResult);
        return;
      }

      // Convert typed data to wagmi format
      const typedData = prepareResult.typedData as any;

      let domain, types, primaryType, message;

      if (typedData.domain && typedData.types && typedData.message) {
        domain = typedData.domain;
        types = typedData.types;
        primaryType =
          typedData.primaryType || typedData.primaryType || "AgentApproval";
        message = typedData.message;
      } else {
        domain = typedData.domain || {};
        types = typedData.types || typedData.Types || {};
        primaryType =
          typedData.primaryType || typedData.PrimaryType || "AgentApproval";
        message = typedData.message || typedData.Message || typedData;
      }

      // Sign with wagmi (supports Coinbase Wallet via injected connector)
      const signature = await signTypedDataAsync({
        domain,
        types,
        primaryType,
        message,
      });

      setResult({
        success: true,
        typedData: prepareResult.typedData,
        signature: signature,
      });
    } catch (error: any) {
      // Handle user rejection
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected")
      ) {
        setResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        setResult({
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Unknown error during Coinbase Wallet signing",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Sign agent approval with Phantom Solana wallet
  const signAgentApprovalWithPhantomSolana = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner address and agent address are required",
      });
      return;
    }

    // Check if Phantom Solana provider is available
    if (!isPhantomSolanaAvailable()) {
      setResult({
        success: false,
        error: "Phantom Solana wallet not found. Please install Phantom wallet extension.",
      });
      return;
    }

    const provider = getPhantomSolanaProvider();
    if (!provider) {
      setResult({
        success: false,
        error: "Phantom Solana provider not available",
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // Ensure connected to Phantom Solana
      const connectedPublicKey = await connectPhantomSolana(provider);

      // Verify the connected address matches the owner address
      if (connectedPublicKey !== ownerAddress) {
        setResult({
          success: false,
          error: `Connected address (${connectedPublicKey}) does not match owner address (${ownerAddress}). Please switch to the correct account in Phantom.`,
        });
        return;
      }

      // Prepare permissions array
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      // Build Solana message payload (similar to EIP-712 structure)
      const messagePayload: SolanaMessagePayload = {
        action: "agent_approval",
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
        timestamp: Math.floor(Date.now() / 1000), // Unix timestamp in seconds
      };

      // Sign message with Phantom Solana
      const signedResult = await signSolanaMessage(provider, messagePayload);

      setResult({
        success: true,
        signature: signedResult.signature,
        typedData: messagePayload, // Store payload for reference
      });
    } catch (error: any) {
      // Handle user rejection
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected") ||
        error.message?.includes("User cancelled")
      ) {
        setResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        setResult({
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Unknown error during Phantom Solana signing",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Sign agent approval with Bitcoin wallet
  const signAgentApprovalWithBitcoin = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner address and agent address are required",
      });
      return;
    }

    // Check if Bitcoin wallet is available
    if (!isBitcoinWalletAvailable()) {
      setResult({
        success: false,
        error: "Bitcoin wallet not found. Please install Unisat, Xverse, or OKX wallet extension.",
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // Get Bitcoin address from wallet (if not already set)
      let bitcoinAddress = ownerAddress;
      try {
        const walletAddress = await getBitcoinAddress();
        // Verify the wallet address matches the owner address
        if (walletAddress !== ownerAddress) {
          setResult({
            success: false,
            error: `Wallet address (${walletAddress}) does not match owner address (${ownerAddress}). Please switch to the correct account in your Bitcoin wallet.`,
          });
          return;
        }
        bitcoinAddress = walletAddress;
      } catch (error) {
        // If address retrieval fails, use the owner address and continue
        console.warn("Could not get address from wallet, using owner address:", error);
      }

      // Prepare permissions array
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      // Build Bitcoin message payload (similar to EIP-712 structure)
      // Include nonce and deadline for replay protection
      const messagePayload: BitcoinMessagePayload = {
        action: "agent_approval",
        owner: bitcoinAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
        timestamp: Math.floor(Date.now() / 1000), // Unix timestamp in seconds
        deadline: (Date.now() + expiresInDays * 24 * 60 * 60 * 1000).toString(16), // Hex deadline
      };

      // Determine preferred signing method (BIP-322 if supported, otherwise BIP-137)
      const wallet = getPreferredBitcoinWallet();
      const preferredMethod = wallet?.supportsBIP322 ? 'bip322' : 'bip137';

      // Sign message with Bitcoin wallet
      const signedResult = await signBitcoinMessage(
        messagePayload,
        bitcoinAddress,
        preferredMethod
      );

      setResult({
        success: true,
        signature: signedResult.signature,
        typedData: messagePayload, // Store payload for reference
      });
    } catch (error: any) {
      // Handle user rejection
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected") ||
        error.message?.includes("User cancelled") ||
        error.message?.includes("user reject")
      ) {
        setResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        setResult({
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Unknown error during Bitcoin signing",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Chain-aware signing method that routes to appropriate signing function
  const signAgentApprovalForChain = async (): Promise<void> => {
    switch (ownerChainType) {
      case "ethereum":
        // Use existing Ethereum signing (MetaMask/Phantom Ethereum/OneKey/Bitget/TokenPocket/Coinbase)
        const walletType = getConnectedWalletType();
        if (walletType === "phantom") {
          return signAgentApprovalWithPhantom();
        } else if (walletType === "onekey") {
          return signAgentApprovalWithOneKey();
        } else if (walletType === "bitget") {
          return signAgentApprovalWithBitget();
        } else if (walletType === "tokenpocket") {
          return signAgentApprovalWithTokenPocket();
        } else if (walletType === "coinbase") {
          return signAgentApprovalWithCoinbaseWallet();
        } else {
          return signAgentApproval();
        }
      case "solana":
        return signAgentApprovalWithPhantomSolana();
      case "bitcoin_taproot":
      case "bitcoin_segwit":
      case "bitcoin_legacy":
      case "bitcoin_nested_segwit":
        return signAgentApprovalWithBitcoin();
      default:
        setResult({
          success: false,
          error: `Signing not yet supported for chain type: ${ownerChainType}`,
        });
    }
  };

  // Check if MetaMask is connected via wagmi (consistent with Phantom and OneKey)
  const isMetaMaskConnected =
    isConnected &&
    getConnectedWalletType() === "metamask" &&
    connectedAddress?.toLowerCase() === ownerAddress.toLowerCase();

  // Check if Phantom Solana is connected
  const isPhantomSolanaConnected = (): boolean => {
    if (ownerChainType !== "solana") return false;
    const provider = getPhantomSolanaProvider();
    if (!provider || !provider.isConnected) return false;
    if (!provider.publicKey) return false;
    return provider.publicKey.toString() === ownerAddress;
  };

  // Check if Bitcoin wallet is connected (synchronous check)
  const isBitcoinWalletConnected = (): boolean => {
    const isBitcoinChain = 
      ownerChainType === "bitcoin_taproot" ||
      ownerChainType === "bitcoin_segwit" ||
      ownerChainType === "bitcoin_legacy" ||
      ownerChainType === "bitcoin_nested_segwit";
    
    if (!isBitcoinChain) return false;
    if (!isBitcoinWalletAvailable()) return false;
    if (!ownerAddress) return false;

    // Note: Full verification requires async call to getBitcoinAddress()
    // This is a best-effort synchronous check
    // The actual address verification happens in signAgentApprovalWithBitcoin()
    return true;
  };

  // Clear agent credentials from both state and sessionStorage
  const clearAgentCredentials = (): void => {
    setAgentAddress("");
    setAgentPrivateKey("");
    try {
      sessionStorage.removeItem(STORAGE_KEY_AGENT_ADDRESS);
      sessionStorage.removeItem(STORAGE_KEY_AGENT_PRIVATE_KEY);
      sessionStorage.removeItem(STORAGE_KEY_AGENT_EXPIRES_AT);
      sessionStorage.removeItem(STORAGE_KEY_AGENT_OWNER_ADDRESS);
    } catch (error) {
      console.warn("Failed to clear agent credentials from sessionStorage:", error);
    }
  };

  return {
    ownerMnemonic,
    setOwnerMnemonic,
    ownerAddress,
    setOwnerAddress,
    ownerChainType,
    setOwnerChainType,
    ownerAddressIndex,
    setOwnerAddressIndex,
    ownerIsMainnet,
    setOwnerIsMainnet,
    agentAddress,
    setAgentAddress,
    agentPrivateKey,
    setAgentPrivateKey,
    expiresInDays,
    setExpiresInDays,
    permissions,
    setPermissions,
    agentName,
    setAgentName,
    nonce,
    setNonce,
    result,
    loading: loading || isSigning,
    generateOwnerAddress,
    generateOwnerAddressForChain,
    generateAgentKeypair,
    prepareAgentSign,
    signAgentApproval,
    signAgentApprovalWithPhantom,
    signAgentApprovalWithOneKey,
    signAgentApprovalWithBitget,
    signAgentApprovalWithTokenPocket,
    signAgentApprovalWithCoinbaseWallet,
    signAgentApprovalWithPhantomSolana,
    signAgentApprovalWithBitcoin,
    signAgentApprovalForChain,
    clearAgentCredentials,
    isMetaMaskConnected,
    isPhantomConnected:
      isConnected &&
      getConnectedWalletType() === "phantom" &&
      ownerChainType === "ethereum" &&
      connectedAddress?.toLowerCase() === ownerAddress.toLowerCase(),
    isPhantomSolanaConnected: isPhantomSolanaConnected(),
    isOneKeyConnected:
      isConnected &&
      getConnectedWalletType() === "onekey" &&
      connectedAddress?.toLowerCase() === ownerAddress.toLowerCase(),
    isOKXConnected:
      isConnected &&
      getConnectedWalletType() === "okx" &&
      connectedAddress?.toLowerCase() === ownerAddress.toLowerCase(),
    isBinanceConnected:
      isConnected &&
      getConnectedWalletType() === "binance" &&
      connectedAddress?.toLowerCase() === ownerAddress.toLowerCase(),
    isBitgetConnected:
      isConnected &&
      getConnectedWalletType() === "bitget" &&
      connectedAddress?.toLowerCase() === ownerAddress.toLowerCase(),
    isTokenPocketConnected:
      isConnected &&
      getConnectedWalletType() === "tokenpocket" &&
      connectedAddress?.toLowerCase() === ownerAddress.toLowerCase(),
    isCoinbaseWalletConnected:
      isConnected &&
      getConnectedWalletType() === "coinbase" &&
      connectedAddress?.toLowerCase() === ownerAddress.toLowerCase(),
    isRainbowConnected:
      isConnected &&
      getConnectedWalletType() === "rainbow" &&
      connectedAddress?.toLowerCase() === ownerAddress.toLowerCase(),
    isPhantomSolanaAvailable: isPhantomSolanaAvailable(),
    isBitcoinWalletAvailable: isBitcoinWalletAvailable(),
    isBitcoinWalletConnected: isBitcoinWalletConnected(),
    isReady: mormSigner.isReady(),
  };
}

// Note: All wallet detection functions are now imported from extdetection.ts
// They are re-exported above for backward compatibility
